/**
 * 64-bit hex digests over Web Crypto.
 *
 * The reference site (fingerprint-scan.com) renders fingerprint parts as 16
 * lowercase hex chars. We match that exactly so parts are directly comparable
 * between the two tools.
 */

const encoder = new TextEncoder();

/** SHA-256, hex encoded, full 64 chars. */
export async function sha256Hex(input: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(input));
  return toHex(new Uint8Array(digest));
}

/**
 * Truncated SHA-256: first 8 bytes, lowercase hex.
 * A collision here means two devices share a fingerprint *part*, which is a
 * legitimate outcome (iPhones with identical hardware), not a defect.
 */
export async function shortHash(input: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(input));
  const bytes = new Uint8Array(digest).slice(0, 8);
  return toHex(bytes);
}

/** SHA-256 of a structured value, with object keys sorted for stability. */
export async function hashValue(value: unknown): Promise<string> {
  return shortHash(stableStringify(value));
}

function toHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

/**
 * JSON.stringify with sorted object keys, so that two structurally equal values
 * always produce the same string regardless of property insertion order.
 */
export function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : 'null';
  }
  if (typeof value === 'boolean' || typeof value === 'string') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (value instanceof Set) {
    return stableStringify([...value].sort());
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
  }
  // functions, symbols, bigint
  return 'null';
}
